<template>
  <div class="rubric">
    <div class="rubric__head">
      <h2
        class="rubric__title"
        :class="{
          'rubric__title--no-vertival-offsets': noVerticalOffsets,
          'rubric__title--centred': isCentred
        }"
      >
        <slot name="title" />
      </h2>

      <div
        v-if="$slots.headRight || enableSwDesktop"
        class="rubric__right"
      >
        <slot name="headRight" />

        <div v-if="enableSwDesktop" class="rubric__arrows show-from-tablet">
          <div ref="prev-el" class="rubric__arrow icon-chevron-left-solid" />
          <div ref="next-el" class="rubric__arrow icon-chevron-right-solid" />
        </div>
      </div>
    </div>

    <div
      ref="container-ref"
      class="rubric__container swiper-container"
      :class="{
        'rubric__container--no-vertival-offsets': noVerticalOffsets
      }"
    >
      <div
        class="swiper-wrapper"
        :class="[
          enableSwDesktop ? 'rubric__swiper' : 'rubric__list',
          isCentred ? 'rubric__list--centred' : ''
        ]"
      >
        <ProductCardSmall
          v-for="(product, index) in products"
          :key="`${product.uuid}_${index}`"
          class="rubric__list-item swiper-slide"
          :product="product"
          :block="block"
          :is-offer="isOffer"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Swiper, Navigation, Keyboard } from 'swiper'
import type { ProductCatalog } from '~/types/product'

Swiper.use([Navigation, Keyboard])

type Props = {
  products: ProductCatalog[]
  block: string
  noVerticalOffsets?: boolean
  isOffer?: boolean
  enableSwDesktop?: boolean
  isCentred?: boolean
}

const props = defineProps<Props>()

const swiperInstance = ref<Swiper>()
const isCarouselInitialized = shallowRef(false)

const productsContainer = useTemplateRef<HTMLDivElement>('container-ref')
const prevEl = useTemplateRef<HTMLDivElement>('prev-el')
const nextEl = useTemplateRef<HTMLDivElement>('next-el')

const initSwiper = () => {
  const options: any = {}

  if (props.enableSwDesktop) {
    options.keyboard = true
    options.navigation = {
      prevEl: prevEl.value,
      nextEl: nextEl.value
    }
    options.breakpoints = {
      740: {
        slidesPerView: 4
      },
      1024: {
        slidesPerGroup: 2,
        slidesPerView: 6,
        freeMode: false
      },
      1300: {
        slidesPerGroup: 2,
        slidesPerView: 8,
        freeMode: false
      }
    }
  }

  swiperInstance.value = new Swiper(productsContainer.value as HTMLDivElement, {
    slidesPerView: 'auto',
    spaceBetween: 10,
    watchOverflow: true,
    freeMode: true,
    freeModeMomentumRatio: 0.5,
    freeModeMomentumBounceRatio: 0.4,
    freeModeMinimumVelocity: 0.1,
    freeModeSticky: true,
    observer: true,
    resizeObserver: true,
    updateOnWindowResize: true,
    ...options,
    on: {
      init: () => {
        isCarouselInitialized.value = true
      }
    }
  })
}

const onResize = () => {
  if (props.enableSwDesktop) {
    // if (!this.isCarouselInitialized) {
    initSwiper()
    // }
    return
  }
  const docElem = document.documentElement
  const body = document.getElementsByTagName('body')[0]
  const width = window.innerWidth || docElem.clientWidth || body.clientWidth

  if (width > 740 && isCarouselInitialized.value) {
    isCarouselInitialized.value = false
    swiperInstance.value!.destroy(true, false)

    productsContainer.value!
      .querySelector('.swiper-wrapper')!
      .removeAttribute('style')
  } else if (width <= 740 && !isCarouselInitialized.value) {
    initSwiper()
  }
}

if (!props.enableSwDesktop) {
  useResizeTimeout(onResize)
}

onMounted(onResize)
onBeforeUnmount(() => {
  if (isCarouselInitialized.value) {
    swiperInstance.value?.destroy(true, true)
  }
})
</script>

<style lang="scss" scoped>
.rubric {
  margin-top: 6rem;

  @media only screen and (max-width: 740px) {
    margin-top: 24px;
  }

  @media only screen and (min-width: 740px) and (max-width: 1024px) {
    margin-top: 4rem;
  }

  &__head {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &__title {
    font-size: $h3-font-size;
    line-height: $h3-line-height;
    color: $font-color;
    font-weight: 600;
    margin: 0 20px;
    position: relative;

    &--no-vertival-offsets {
      margin: 0;
    }

    @media only screen and (min-width: 740px) {
      margin: 0;
    }

    @media only screen and (max-width: 740px) {
      font-size: $h4-font-size;
      line-height: 2.2rem;
    }

    &--centred {
      width: 100%;
      text-align: center;
    }
  }

  &__right {
    margin-right: 1.6rem;
  }

  &__container {
    margin: 20px 0 0 0;
    line-height: 0;

    @media only screen and (max-width: 740px) {
      position: relative;
      display: block;
      white-space: nowrap;
      overflow: hidden;
      width: 100%;
      padding-top: 5px;
      padding-bottom: 5px;
      margin-bottom: -5px;
      margin-top: 15px;

      &:not(&--no-vertival-offsets) {
        padding-left: 20px;
        padding-right: 20px;
      }
    }
  }

  &__list {
    @media only screen and (min-width: 740px) {
      display: grid;
      grid-gap: 10px;
      grid-template-columns: repeat(8, 1fr);
    }

    @media only screen and (min-width: 740px) and (max-width: 1560px) {
      grid-template-columns: repeat(auto-fill, minmax(144px, 1fr));
    }

    &--centred {
      @media only screen and (min-width: 740px) {
        display: flex;
        justify-content: center;
        flex-wrap: wrap;
        .rubric__list-item {
          width: calc(100% / 8);
          min-width: 144px;
        }
      }
      @media only screen and (min-width: 740px) and (max-width: 1300px) {
        .rubric__list-item {
          width: calc(100% / 8) !important;
        }
      }
      @media only screen and (min-width: 740px) and (max-width: 820px) {
        .rubric__list-item {
          width: calc(100% / 4) !important;
        }
      }
      @media only screen and (min-width: 821px) and (max-width: 974px) {
        .rubric__list-item {
          width: calc(100% / 5) !important;
        }
      }
      @media only screen and (min-width: 974px) and (max-width: 1128px) {
        .rubric__list-item {
          width: calc(100% / 6) !important;
        }
      }
      @media only screen and (min-width: 1128px) and (max-width: 1281px) {
        .rubric__list-item {
          width: calc(100% / 7) !important;
        }
      }
      @media only screen and (min-width: 1282px) {
        .rubric__list-item {
          width: calc(100% / 8) !important;
        }
      }
    }
  }
  &__swiper {
    .rubric__list-item {
      @media only screen and (min-width: 1024px) {
        width: calc(100% / 6);
      }
      @media only screen and (min-width: 1300px) {
        width: calc(100% / 8);
      }
      //@media only screen and (min-width: 740px) {
      //  height: auto;
      //  vertical-align: top;
      //  line-height: initial;
      //}
    }
  }

  &__list-item {
    @media only screen and (max-width: 740px) {
      height: auto;
      width: 144px;
      vertical-align: top;
      line-height: initial;
    }
  }

  &__arrows {
    display: flex;
    transform: translateX(1.6rem);
  }

  &__arrow {
    border: 1px solid #e7e7e7;
    box-sizing: border-box;
    border-radius: 8px;
    width: 3.6rem;
    height: 3.6rem;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 1rem;
    cursor: pointer;
    &:first-of-type {
      margin-right: 1.6rem;
    }
  }
}

@media only screen and (min-width: 740px) {
  .swiper-container {
    overflow: visible;
  }
}
</style>
